import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';

export interface DiscussionThreadData {
  discussion: {
    id: string;
    title: string;
    content: string;
    category: string;
    created_at: string;
    updated_at: string;
    likes: number;
    replies: number;
    views: number;
    is_hot: boolean;
    user_id: string;
    openai_thread_id: string | null;
    author: {
      id: string;
      first_name: string;
      profile_picture_url: string | null;
    };
    is_liked_by_user: boolean;
  };
  replies: Array<{
    id: string;
    content: string;
    created_at: string;
    likes: number;
    user_id: string | null;
    is_ai_response: boolean;
    assistant_id: string | null;
    is_liked_by_user: boolean;
    author: {
      id: string;
      first_name: string;
      profile_picture_url: string | null;
    } | null;
    ai_assistant: {
      id: string;
      name: string;
      openai_assistant_id: string;
      profile_picture_url: string | null;
    } | null;
  }>;
}

export const useDiscussionThreadData = (discussionId: string) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['discussion-thread-data', discussionId, user?.id],
    queryFn: async (): Promise<DiscussionThreadData> => {
      if (!discussionId) throw new Error('Discussion ID is required');
      
      // Call the PostgreSQL function using RPC
      const { data, error } = await supabase.rpc('get_discussion_thread_data', {
        p_discussion_id: discussionId,
        p_user_id: user?.id || null
      });

      if (error) {
        console.error('RPC Error:', error);
        console.error('RPC Parameters:', {
          p_discussion_id: discussionId,
          p_user_id: user?.id || null
        });
        throw error;
      }

      if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error('Discussion not found');
      }

      // The PostgreSQL function returns flattened rows that need to be transformed
      // First row contains the discussion data, and each row may contain reply data
      const firstRow = data[0];
      
      // Extract discussion data (same in all rows)
      const discussion = {
        id: firstRow.discussion_id,
        title: firstRow.discussion_title,
        content: firstRow.discussion_content,
        category: firstRow.discussion_category,
        created_at: firstRow.discussion_created_at,
        updated_at: firstRow.discussion_updated_at,
        likes: firstRow.discussion_likes,
        replies: firstRow.discussion_replies,
        views: firstRow.discussion_views,
        is_hot: firstRow.discussion_is_hot,
        user_id: firstRow.discussion_user_id,
        openai_thread_id: firstRow.discussion_openai_thread_id,
        author: {
          id: firstRow.discussion_author_id,
          first_name: firstRow.discussion_author_first_name,
          profile_picture_url: firstRow.discussion_author_profile_picture_url
        },
        is_liked_by_user: firstRow.discussion_is_liked_by_user
      };

      // Extract and deduplicate replies data
      const repliesMap = new Map();
      
      for (const row of data) {
        // Skip rows without reply data (discussion-only rows)
        if (!row.reply_id) continue;
        
        if (!repliesMap.has(row.reply_id)) {
          repliesMap.set(row.reply_id, {
            id: row.reply_id,
            content: row.reply_content,
            created_at: row.reply_created_at,
            likes: row.reply_likes,
            user_id: row.reply_user_id,
            is_ai_response: row.reply_is_ai_response,
            assistant_id: row.reply_assistant_id,
            is_liked_by_user: row.reply_is_liked_by_user,
            author: row.reply_author_id ? {
              id: row.reply_author_id,
              first_name: row.reply_author_first_name,
              profile_picture_url: row.reply_author_profile_picture_url
            } : null,
            ai_assistant: row.reply_ai_assistant_id ? {
              id: row.reply_ai_assistant_id,
              name: row.reply_ai_assistant_name,
              openai_assistant_id: row.reply_ai_assistant_openai_id,
              profile_picture_url: row.reply_ai_assistant_profile_picture_url
            } : null
          });
        }
      }

      // Convert replies map to array, sorted by creation time
      const replies = Array.from(repliesMap.values()).sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      return {
        discussion,
        replies
      };
    },
    enabled: !!discussionId,
  });
}; 